import { Button, Box, Typography, Card, CardContent, Checkbox, Avatar, Stack, Grid, Input } from "@mui/joy";
import { Building2, CheckCircle2, Search } from "lucide-react";
import { useState, useMemo } from "react";

import { SideModal } from "../";
import { SupplierComparison as PriceComparison } from "../Supplier/PriceComparison";
import { useSupplierCompareInit } from "./hooks/useSupplierCompareInit";
import { useSupplierCompareUpdate } from "./hooks/useSupplierCompareUpdate";
import Alert from "../../shared/Alert";
import { SupplierComparisonSkeleton } from "./Skeleton";

interface SupplierComparisonRefreshProps {
    selectedValues: Record<string, any>;
    type?: string;
}

export function SupplierComparisonRefresh({ selectedValues, type = "Manufacturer" }: SupplierComparisonRefreshProps) {
    const {
        suppliers,
        selectedSupplierNames,
        setSelectedSupplierNames,
        openModal,
        setOpenModal,
        comparisonResults,
        isComparing,
        itemSupplierSelections,
        handleCompare,
        isLoadingSuppliers,
    } = useSupplierCompareInit({ selectedValues, type });

    const { localSelections, onItemSupplierSelect, handleConfirm } = useSupplierCompareUpdate(
        itemSupplierSelections,
        openModal,
        selectedValues,
        suppliers,
        comparisonResults,
        setOpenModal,
    );

    const [searchTerm, setSearchTerm] = useState("");

    const filteredSuppliers = useMemo(() => {
        if (!searchTerm) return suppliers;
        return suppliers.filter((s: any) => s.Name.toLowerCase().includes(searchTerm.toLowerCase()));
    }, [suppliers, searchTerm]);

    const displaySuppliers = filteredSuppliers.slice(0, 5);
    const remainingCount = filteredSuppliers.length - 5;

    if (isLoadingSuppliers) return <SupplierComparisonSkeleton />;

    return (
        <Box
            mt={2}
            sx={{
                bgcolor: "background.surface",
                p: 2,
                py: 2,
                borderRadius: "lg",
                border: "1px solid",
                borderColor: "divider",
            }}
        >
            <Box mb={1.5}>
                <Box
                    mb={2}
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-start",
                        flexWrap: "wrap",
                        gap: 2,
                    }}
                >
                    <Box>
                        <Typography level="title-md" fontWeight="bold">
                            Supplier Architecture & Comparison
                        </Typography>
                        <Typography level="body-sm" color="neutral">
                            Select {type.toLowerCase()}s to evaluate pricing for this project.
                        </Typography>
                    </Box>
                    <Input
                        size="sm"
                        placeholder="Search suppliers..."
                        startDecorator={<Search size={16} />}
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        sx={{ minWidth: { xs: "100%", sm: 250 } }}
                    />
                </Box>

                {selectedSupplierNames.length === 0 && (
                    <Alert
                        errorMessage="Select at least one supplier to perform a market comparison."
                        sx={{ fontSize: "xs", py: 0.25, minHeight: 0, mb: 2 }}
                    />
                )}

                <Grid container spacing={1} mt={selectedSupplierNames.length === 0 ? 2 : 0}>
                    {displaySuppliers.map((sup: any) => {
                        const isSelected = selectedSupplierNames.includes(sup.Name);
                        return (
                            <Grid key={sup.Id} xs={12} sm={4} md={3}>
                                <Card
                                    component="label"
                                    variant={isSelected ? "soft" : "outlined"}
                                    color={isSelected ? "primary" : "neutral"}
                                    sx={{
                                        cursor: "pointer",
                                        "&:hover": {
                                            borderColor: isSelected ? "primary.solidBg" : "neutral.outlinedBorder",
                                            bgcolor: isSelected ? "primary.softHoverBg" : "background.level1",
                                        },
                                        position: "relative",
                                        overflow: "visible",
                                        border: isSelected ? "2px solid" : "1px solid",
                                        borderColor: isSelected ? "primary.solidBg" : "divider",
                                        minHeight: "auto",
                                        py: 0.5, // Tight Y padding
                                    }}
                                >
                                    <Box
                                        sx={{
                                            position: "absolute",
                                            top: -5,
                                            right: -5,
                                            display: isSelected ? "flex" : "none",
                                            bgcolor: "primary.solidBg",
                                            color: "primary.solidColor",
                                            borderRadius: "50%",
                                            p: 0.25,
                                            zIndex: 1,
                                            border: "1px solid",
                                            borderColor: "background.surface",
                                        }}
                                    >
                                        <CheckCircle2 size={12} strokeWidth={3} />
                                    </Box>

                                    <Checkbox
                                        checked={isSelected}
                                        onChange={(e) => {
                                            if (e.target.checked) {
                                                setSelectedSupplierNames((prev: string[]) => [...prev, sup.Name]);
                                            } else {
                                                setSelectedSupplierNames((prev: string[]) =>
                                                    prev.filter((name) => name !== sup.Name),
                                                );
                                            }
                                        }}
                                        sx={{ display: "none" }}
                                    />
                                    <Box sx={{ p: 0.5, px: 1 }}>
                                        <Stack direction="row" spacing={1} alignItems="center">
                                            <Avatar
                                                variant={isSelected ? "solid" : "soft"}
                                                color={isSelected ? "primary" : "neutral"}
                                                size="sm"
                                                sx={{ "--Avatar-size": "24px" }}
                                            >
                                                <Building2 size={14} />
                                            </Avatar>
                                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                                <Typography level="title-sm" noWrap>
                                                    {sup.Name}
                                                </Typography>
                                                <Typography level="body-xs" color="neutral">
                                                    Open Market
                                                </Typography>
                                            </Box>
                                        </Stack>
                                    </Box>
                                </Card>
                            </Grid>
                        );
                    })}
                    {remainingCount > 0 && (
                        <Grid xs={12} sm={4} md={3}>
                            <Card
                                variant="soft"
                                color="neutral"
                                sx={{
                                    height: "100%",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    border: "1px dashed",
                                    borderColor: "divider",
                                    bgcolor: "background.level1",
                                    py: 0.5, // Match supplier card Y-padding
                                    minHeight: "auto",
                                }}
                            >
                                <Typography level="body-xs" fontWeight="bold" color="neutral">
                                    + {remainingCount} more remaining
                                </Typography>
                            </Card>
                        </Grid>
                    )}
                </Grid>
            </Box>

            <Button
                variant="soft"
                size="sm"
                color="primary"
                onClick={handleCompare}
                loading={isComparing}
                sx={{ border: "1px solid", borderColor: "divider" }}
                disabled={selectedSupplierNames.length === 0}
            >
                Compare Suppliers
            </Button>

            <SideModal
                portalBoxProps={{ minWidth: "70%", width: 1200, maxWidth: "100vw" }}
                open={openModal}
                onClose={() => setOpenModal(false)}
            >
                <PriceComparison
                    comparisonResults={comparisonResults}
                    isLoading={isComparing}
                    onClose={() => setOpenModal(false)}
                    itemSupplierSelections={localSelections}
                    onItemSupplierSelect={onItemSupplierSelect}
                    onConfirm={handleConfirm}
                    purchaseStrategy="multiple"
                />
            </SideModal>
        </Box>
    );
}

export default SupplierComparisonRefresh;
